<template>
  <div class="h-full flex flex-col overflow-hidden">
    <!-- Content Wrapper -->
    <div class="flex h-full" style="height: calc(100vh - 60px);">
      <!-- Loading -->
      <div v-if="isLoading" class="w-full h-full flex justify-center items-center py-10">
        <div class="flex flex-col items-center gap-4">
          <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          <p class="text-gray-600">Kategoriler yükleniyor...</p>
        </div>
      </div>

      <!-- Content -->
      <template v-else>
        <!-- Left Panel - Categories Tree - Mobil'de seçim yapılınca gizlenir -->
        <div :class="['border-r overflow-auto bg-gray-50 h-full flex flex-col min-h-screen thin-scrollbar',
                     windowWidth < 768 && selectedCategory ? 'hidden' : 'block', 
                     windowWidth < 768 ? 'w-full' : 'w-1/3']">
          <CategoryTreePanel :categories="categories" :selected-category-id="selectedCategory?.id" :changed-categories="changedCategories" :is-saving="isSaving" @select="selectCategory" @save="saveAllChanges"/>
        </div>

        <!-- Right Panel - Prompt Editor - Mobil'de kategori seçilince gösterilir -->
        <div :class="['p-6 overflow-auto bg-white h-full flex flex-col min-h-screen',
                     windowWidth < 768 && !selectedCategory ? 'hidden' : 'block',
                     windowWidth < 768 ? 'w-full' : 'w-2/3']">
          <CategoryPromptEditor :category="selectedCategory" :window-width="windowWidth" :changed-categories="changedCategories" @back="selectedCategory = null" @update:prompt="handlePromptUpdate"/>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue'
import { useAssetStore } from '@/stores/asset'
import { responseHandlerService } from '@/plugins/responseHandlerService'
import CategoryTreePanel from './CategoryTreePanel.vue'
import CategoryPromptEditor from './CategoryPromptEditor.vue'
import { saveChanges, fetchPrompt } from '../../services/prompt'

const props = defineProps({
  // Tüm kategoriler (kategori modalından alınır)
  allCategories: {
    type: Array,
    default: () => [],
  },

  // Seçili kategori (opsiyonel)
  selectedCategory: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['close'])

const assetStore = useAssetStore()
const windowWidth = ref(window.innerWidth)
const isLoading = ref(false)
const isSaving = ref(false)
const categories = ref([])
const selectedCategory = ref(props.selectedCategory || null)

// Değiştirilen kategorileri takip etmek için
const changedCategories = ref(new Set())

// Props'tan gelen kategorileri yerel state'e kopyala
watch(() => props.allCategories, (newCategories) => {
  categories.value = newCategories || []

  // Seçili kategori varsa güncelle
  if (props.selectedCategory) {
    selectCategory(props.selectedCategory)
  }
}, { immediate: true })

// Kategori seç
const selectCategory = (category) => {
  selectedCategory.value = category
}

// Prompt güncellendiğinde
const handlePromptUpdate = ({ categoryId, hasChanges }) => {
  // Değişiklik yok, changedCategories'den kaldır
  if (!hasChanges) {
    changedCategories.value.delete(categoryId)
  }
}

// Tüm değişiklikleri kaydet
const saveAllChanges = () => {
  if (changedCategories.value.size === 0) return

  isSaving.value = true

  saveChanges(
      assetStore.assetId,
      categories.value,
      changedCategories.value
  )
  .then(result => {
    if (result.success) {
      // Tüm değişiklikleri temizle
      changedCategories.value.clear()

      // Seçili kategori varsa prompt bilgilerini yenile
      if (selectedCategory.value) {
        fetchPrompt(selectedCategory.value, assetStore.assetId)
      }
    }
  })
  .finally(() => {
    isSaving.value = false
  })
}

// Pencere boyutu değiştiğinde
const handleResize = () => {
  windowWidth.value = window.innerWidth
}

// Lifecycle hooks
onMounted(() => {
  window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
})
</script>